import { config } from 'config';
import { svg } from '../utils/svg.js';
import { number } from '../utils/number.js';
import { circle } from '../utils/circle.js';
import { dataSeries, RANGE_MIXED, RANGE_NEGATIVE, RANGE_POSITIVE, RANGE_ZERO } from '../dataSeries.js';

/**
 *
 * @module gui/legendProportional
 */
export const legendProportional = {

  /**
   * ID of the div containing the value for the active region
   * @type String
   * @private
   */
  _rowNodeCurrentId: 'row-element',

  /**
   * DOM element of the div containing the value for the active region
   * @type {?HTMLDivElement}
   * @private
   */
  _rowNodeCurrent: null,

  /**
   * default circle centerX
   */
  centerX: config.circleMaxRadius + 1,

  /**
   * The svg element
   * @type {?SVGSVGElement}
   * @private
   */
  _svg: null,

  /**
   * How many legend circles should get created?
   * @type {Number}
   * @private
   */
  countCircles: 4,

  /**
   * The area of the biggest circle
   * @type {Number}
   * @private
   */
  _maxArea: null,

  /**
   * ID of the highlight circle for the hovered region
   * @type {String}
   * @private
   */
  _highlightCircleId: 'highlight-circle',

  /**
   * Create and return the circles.
   * @param {DataSeries} data
   * @return {SVGSVGElement}
   */
  create (data) {
    let circle, line, text, midline;

    this._svg = svg.create('svg', {
      class: 'legend-data-svg'
    });
    this.domNode.append(this._svg); // otherwise, we can't calculate the bounding boxes.
    const root = svg.create('g');
    this._svg.append(root);

    // add a dummy text to be able to calc the text height.
    const dummyTxt = svg.createText(0, 50, 'Dummy');
    root.append(dummyTxt);
    this._textHeight = this._calcTextHeight(this._svg.firstElementChild);
    midline = this._calcMidline(data.min, data.max, data.range);
    dummyTxt.remove();

    // add the zero stop
    line = this._createLine({ radius: 0, stop: 0 }, midline);
    text = this._createText({ radius: 0, stop: 0 }, midline);
    root.append(line, text);

    // add the other circles and stops
    const circleData = this._calcCircleData(data.min, data.max, data.range);
    circleData.forEach(item => {
      circle = this._createCircle(item, midline);
      if (data.range !== RANGE_ZERO) {
        line = this._createLine(item, midline);
        text = this._createText(item, midline);
      }
      root.append(circle, line, text);
    });
    this._setDim(this._svg, data.range);

    return this._svg;
  },

  /**
   * Initialize the static part of the legend.
   * @private
   */
  _initDom () {
    this._rowNodeCurrent = document.createElement('div');
    this._rowNodeCurrent.id = this._rowNodeCurrentId;
    this._rowNodeCurrent.classList.add('legend__row-value');
    this.contentNode.append(this._rowNodeCurrent);
  },

  /**
   * Set the width and height of the SVG based on its content.
   * @param {SVGSVGElement} svg
   * @param {Number} rangeType
   * @private
   */
  _setDim (svg, rangeType) {
    const box = svg.getBBox();
    let w = Math.ceil(box.width) + 5;
    let h = Math.ceil(box.height);
    if (rangeType === RANGE_ZERO) {
      // correct the height, because there is no max stop
      h += this._textHeight / 2;
    }
    svg.setAttribute('width', w);
    svg.setAttribute('height', h);
  },

  /**
   * Create the SVG circle element.
   * @returns {SVGCircleElement}
   * @private
   * @param {LegendCircleData} data
   * @param {Number} midline
   */
  _createCircle (data, midline) {
    let cy;
    if (data.stop >= 0) {
      cy = midline - data.radius;
    } else {
      cy = midline + data.radius;
    }
    const circle = svg.createCircle(this.centerX, cy, data.radius);
    circle.classList.add(...this.getCssClass(data.stop));
    return circle;
  },

  /**
   * Return the CSS class based on the sign of the item value.
   * @param {Number} value the value of the data item
   * @return {string[]}
   */
  getCssClass (value) {
    let clCss = ['legend-data-svg__circle', 'svg-circle'];
    switch (Math.sign(value)) {
      case 1:
        clCss.push('svg-circle--positive');
        break;
      case -1:
        clCss.push('svg-circle--negative');
        break;
      default:
        clCss.push('svg-circle--zero');
    }

    return clCss;
  },

  /**
   * Creates a SVG line element.
   * @returns {SVGLineElement}
   * @private
   * @param {LegendCircleData} data
   * @param {Number} midline
   */
  _createLine (data, midline) {
    let y, x2;
    x2 = this.centerX + config.circleMaxRadius + 5;
    if (data.stop >= 0) {
      y = midline - 2 * data.radius;
    } else {
      y = midline + 2 * data.radius;
    }
    const line = svg.createLine(this.centerX, y, x2, y);
    line.classList.add('svg-line');

    return line;
  },

  /**
   * Creates a SVG text element.
   * @returns {SVGTextElement}
   * @private
   * @param {LegendCircleData} data
   * @param midline
   */
  _createText (data, midline) {
    let x, y;
    x = this.centerX + config.circleMaxRadius + 12;
    if (data.stop > 0) {
      y = midline - 2 * data.radius;
    } else {
      y = midline + 2 * data.radius;
    }
    y += Math.round(this._textHeight / 4);

    return svg.createText(x, y, String(data.stop));
  },

  /**
   * Create an array of default positive circles from the data
   * @returns {Array<LegendCircleData>}
   * @private
   * @param {Number} min
   * @param {Number} max
   * @param {Number} rangeType
   */
  _calcCircleData (min, max, rangeType) {
    const circleData = [];
    const absMax = number.absMax(min, max);

    switch (rangeType) {
      case RANGE_ZERO:
        circleData.push({
          radius: config.circleMaxRadius,
          stop: 0
        });
        break;
      case RANGE_NEGATIVE:
        this._addCircleData(circleData, min, absMax);
        break;
      case RANGE_POSITIVE:
        this._addCircleData(circleData, max, absMax);
        break;
      case RANGE_MIXED:
        this._addCircleData(circleData, max, absMax);
        this._addCircleData(circleData, min, absMax);
        break;
    }

    return circleData;
  },

  /**
   *
   * @param {Array<LegendCircleData>} circleData
   * @param {Number} startVal the initial value to start creating circles from
   * @param absMax the absolute maximum value in the data series
   * @private
   */
  _addCircleData (circleData, startVal, absMax) {
    let prevRadius, val = startVal; // do not round first stop

    for (let i = 0; i < this.countCircles; i++) {
      const radius = this.calcRadius(val, absMax);
      if (this._isDiscrete(radius, prevRadius)) {
        circleData.push({
          stop: val,
          radius: radius
        });
      }
      val = this._round(val);
      val /= 2;
      prevRadius = radius;
    }
  },

  /**
   * Calculate the radius in relation to the circle with the maximum area.
   * @param {Number} value
   * @param refMaxVal reference maximum value
   * @return {Number}
   * @private
   */
  calcRadius (value, refMaxVal) {
    const refMax = refMaxVal === 0 ? 1 : refMaxVal; // prevent division by zero
    const maxArea = circle.calcArea(config.circleMaxRadius);
    const area = maxArea / refMax * Math.abs(value);

    return Math.floor(circle.calcRadius(area));
  },

  /**
   * Calculate the midline.
   * The midline is drawn at an offset from the top base on the largest (positive) circle.
   * @param {Number} min minimum value in the data series
   * @param {Number} max maximum value in the data series
   * @param {Number} rangeType the type of the range
   * @return {Number}
   * @private
   */
  _calcMidline (min, max, rangeType) {
    let midline;
    switch (rangeType) {
      case RANGE_NEGATIVE:
        midline = 0;
        break;
      case RANGE_MIXED:
        const absMax = number.absMax(min, max);
        midline = max >= Math.abs(min) ? config.circleMaxRadius : this.calcRadius(max, absMax);
        break;
      default:
        midline = config.circleMaxRadius;
    }
    midline *= 2;
    midline += Math.round(this._textHeight / 2) + 2;

    return midline;
  },

  /**
   * Round the value to the nearest power of ten.
   * @param {Number} value
   * @returns {Number}
   * @private
   */
  _round (value) {
    const num = this.reader.getDecimals();
    return number.roundTo(value, num - 1);
  },

  /**
   * Highlight the circle related to the active region.
   * @param {RegionEnterEvent} evt
   * @returns {void}
   */
  highlight (evt) {
    const id = evt.detail.regionId;
    const item = this.rowData.series.find(item => item.id === String(id));
    this._removeHighlightCircle();
    this._rowNodeCurrent.replaceChildren();
    if (item !== undefined) { // a restriction on the region might be set
      if (item.value !== 0) {
        this._appendHighlightCircle(item, this.rowData.min, this.rowData.max);
      }
      this._rowNodeCurrent.innerHTML = dataSeries.createStr(item);
    }
  },

  /**
   * Remove the highlight circle.
   */
  unhighlight () {
    this._removeHighlightCircle();
    this._rowNodeCurrent.replaceChildren();
  },

  /**
   * Add a highlight circle to the svg.
   * @param {DataSeriesItem} region
   * @param {Number} min minimum value in the data series
   * @param {Number} max maximum value in the data series
   * @private
   */
  _appendHighlightCircle (region, min, max) {
    const highlight = document.getElementById(this._highlightCircleId);
    if (highlight !== null) {
      highlight.remove();
    }

    const absMax = number.absMax(min, max);
    const radius = this.calcRadius(region.value, absMax);
    const rangeType = this.rowData.range;
    const midline = this._calcMidline(min, max, rangeType);
    const svgCircle = this._createCircle({ radius: radius, stop: region.value }, midline);
    svgCircle.id = this._highlightCircleId;
    svgCircle.classList.add('svg-circle--highlight');
    this._svg.append(svgCircle);
  },

  /**
   * Remove the highlight circle from the SVG.
   * @private
   */
  _removeHighlightCircle () {
    const currentHighlightCircle = document.getElementById(this._highlightCircleId);
    if (currentHighlightCircle !== null) {
      currentHighlightCircle.remove();
    }
  },

  /**
   * Export the data part of the legend content as SVG.
   * @return {SVGGElement}
   */
  _exportDataSvg () {
    const g = this._svg.firstElementChild.cloneNode(true);
    const bbBox = this._svg.getBBox();
    const txt = this._rowNodeTotal.innerHTML;
    const total = svg.createText(0, bbBox.height + 22, txt, {
      id: this._rowTotalDivId
    });
    g.append(total);
    g.querySelectorAll('circle').forEach(el => {
      el.setAttribute('fill', config.color.circle.posFill);
      el.setAttribute('stroke', config.color.circle.posStroke);
      el.setAttribute('fill-opacity', config.color.circle.opacity);
    });

    return g;
  },

  /**
   * Check if the text stop does not overlap with the previous one.
   * @param {Number} radius radius of the circle currently drawn
   * @param {Number} prevRadius radius of circle previously drawn
   * @return {boolean}
   * @private
   */
  _isDiscrete (radius, prevRadius) {
    const padding = 2;
    if (prevRadius === undefined) {
      return true;
    }

    return Math.abs(prevRadius) - Math.abs(radius) - (this._textHeight / 4) - padding > 0;
  }
};